import { useEffect, useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import FormSectionCompleteIcon from "@mui/icons-material/Done";
import FormSectionNotStartedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import FormSectionPartiallyCompleteIcon from "@mui/icons-material/TripOriginOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  CircularProgress,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from "@mui/material";
import type { TFunction } from "i18next";
import { ok } from "neverthrow";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { match } from "ts-pattern";

import { dayjs, mustBeDefined, mustParseAny } from "@thalamos/common";
import { SubmissionButton } from "@thalamos/frontend";
import type {
  ExtendedVisionUser,
  IncidentFormId,
  IncidentFormSections,
  IncidentWorkItemActionsObject,
  IncidentWorkItemId,
  IncidentWorkItemWithContext,
  RecordPolicePresenceMinimumFields,
  TranslationKey,
  TriageIncidentMinimumFields,
} from "@thalamos/vision-common";
import {
  buildTranslationInput,
  calculateActions,
  calculateIncidentWorkItemStatus,
  defaultActions,
  endIncidentDestination,
  FormSectionSchema,
  getCurrentOwner,
  getLatestPlaceOfSafety,
  getLatestPolicePresence,
  getPatientName,
  getWorkflowTranslationContext,
  hasTriageForm,
  IncidentFormTypeSchema,
  incidentIsActive,
  IncidentWorkItemIdSchema,
  isCurrentOwner,
  isPolicePresenceUpdateDue,
  tk,
  triageSelectionIsMca,
  triageSelectionIsSection136,
  wasTransferredAway,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { createUserSearch } from "../../api/user/createUserSearch.js";
import HospitalIcon from "../../assets/img/hospital_iconfinder.svg";
import PoliceIcon from "../../assets/img/police_security_icon.svg";
import { Banner } from "../../components/Banner/Banner.js";
import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { Footer } from "../../components/FormBuilder/MultiPageForm/Footer.js";
import { Header } from "../../components/FormBuilder/MultiPageForm/Header.js";
import IncidentBanner from "../../components/IncidentBanner/IncidentBanner.js";
import { TypographyI18N } from "../../components/index.js";
import { useUser } from "../../Context/UserContext.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { FormWrapper } from "../../layout/FormWrapper.js";
import { InitiateTransferModal } from "../../modals/InitiateTransfer/InitiateTransfer.js";
import { ReviewBeforeEndingModal } from "../../modals/ReviewBeforeEnding/ReviewBeforeEnding.js";
import { createRoute } from "../../routing/createRoute.js";
import { endIncidentRoute } from "../../routing/endIncidentRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const completionStatus = {
  notStarted: "not-started",
  partiallyComplete: "partially-complete",
  complete: "complete",
} as const;

type CompletionStatus =
  (typeof completionStatus)[keyof typeof completionStatus];

type StepData = {
  label: TranslationKey;
  completionStatus: CompletionStatus;
  onClick?: () => void;
  isDisabled?: boolean;
};

type FormProgressProps = {
  incidentWorkItem?: IncidentWorkItemWithContext;
  incidentWorkItemId?: IncidentWorkItemId;
  steps: StepData[];
  latestPlaceOfSafetyName: string | undefined;
  latestPlaceOfSafetyFormId?: IncidentFormId;
  arrivalUrl?: string;
  latestPolicePresenceName?: RecordPolicePresenceMinimumFields["hasRemainedAtPlaceOfSafety"]["value"];
  latestPolicePresenceUpdatedAt?: Date;
  actions: IncidentWorkItemActionsObject;
  refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
  policePresenceUpdateDueStatus: ReturnType<
    typeof isPolicePresenceUpdateDue
  > | null;
  onCancelTransfer?: (
    incidentWorkItemId: IncidentWorkItemId,
  ) => Promise<{ status: number }>;
  onEndIncident?: (
    incidentWorkItemId: IncidentWorkItemId,
  ) => Promise<{ status: number; data?: { reason?: string } }>;
};

const FormProgressSteps = ({ steps }: { steps: StepData[] }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mx: { xs: 0, sm: 10 }, pt: 2 }}>
      <Stepper orientation="vertical" nonLinear>
        {steps.map((step, index) => (
          <Step
            key={index}
            active={true}
            completed={match(step.completionStatus)
              .with(completionStatus.complete, () => true)
              .with(
                completionStatus.notStarted,
                completionStatus.partiallyComplete,
                () => false,
              )
              .exhaustive()}
          >
            <StepLabel
              icon={match(step.completionStatus)
                .with(completionStatus.notStarted, () => (
                  <FormSectionNotStartedIcon color="action" />
                ))
                .with(completionStatus.partiallyComplete, () => (
                  <FormSectionPartiallyCompleteIcon color="primary" />
                ))
                .with(completionStatus.complete, () => (
                  <FormSectionCompleteIcon color="success" />
                ))
                .exhaustive()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <TypographyI18N
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.8125rem", sm: "1rem" },
                    color: theme.palette.common.black,
                  }}
                  translationKey={step.label}
                />
                <TypographyI18N
                  sx={{
                    color: step.isDisabled
                      ? theme.palette.text.disabled
                      : theme.palette.primary.main,
                    textDecoration: "underline",
                    fontWeight: 500,
                    fontSize: "0.8125rem",
                    cursor: step.isDisabled ? "not-allowed" : "pointer",
                  }}
                  data-testid={`action-button-${index.toString()}`}
                  {...(step.isDisabled ? {} : { onClick: step.onClick })}
                  translationKey={match(step.completionStatus)
                    .with(completionStatus.complete, () =>
                      tk("formProgressPage.view"),
                    )
                    .with(
                      completionStatus.notStarted,
                      completionStatus.partiallyComplete,
                      () => tk("formProgressPage.add"),
                    )
                    .exhaustive()}
                />
              </Box>
              <Divider />
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export const getIncidentStatus = (
  incidentWorkItem: IncidentWorkItemWithContext,
  t: TFunction,
) => {
  const workflowContext = getWorkflowTranslationContext({ incidentWorkItem });
  const tOptions = { context: workflowContext };

  return calculateIncidentWorkItemStatus(incidentWorkItem)
    .map((status) =>
      match(status)
        .with("awaiting-arrival", () =>
          t("formProgressPage.statuses.awaitingArrival", tOptions),
        )
        .with("arrived-at-place-of-safety", () =>
          t("formProgressPage.statuses.arrivedAtPlaceOfSafety", tOptions),
        )
        .with("form-shared", () =>
          t("formProgressPage.statuses.formShared", tOptions),
        )
        .with("nhs-form-accepted", () =>
          t("formProgressPage.statuses.formAccessed", tOptions),
        )
        .with("police-presence-requested", () =>
          t("formProgressPage.statuses.policePresenceRequested", tOptions),
        )
        .with("police-presence-not-requested", () =>
          t("formProgressPage.statuses.policePresenceNotRequested", tOptions),
        )
        .with("unknown", () => t("common.unknown"))
        .with("ended", () => {
          const preEndIncidentForm = incidentWorkItem.forms.find(
            (form) =>
              form.formType === IncidentFormTypeSchema.enum.preEndIncident,
          );

          if (preEndIncidentForm) {
            const preEndIncidentSections = preEndIncidentForm.sections.find(
              (section) => section.id === FormSectionSchema.enum.preEndIncident,
            );

            if (
              preEndIncidentSections?.data?.endingReason ===
              "auto-close-by-anonymiser-job"
            ) {
              return t("formProgressPage.statuses.formAutoClosed");
            }
          }

          return t("formProgressPage.statuses.ended", tOptions);
        })
        .with("in-progress", () =>
          incidentIsActive({ incidentWorkItem }) &&
          triageSelectionIsSection136({ incidentWorkItem })
            ? t("formProgressPage.statuses.s136InProgress", tOptions)
            : status,
        )
        .exhaustive(),
    )
    .unwrapOr(t("common.unknown"));
};

export const extractIncidentDetails = (
  incidentWorkItem: IncidentWorkItemWithContext | undefined,
  t: TFunction,
) => {
  if (incidentWorkItem === undefined) {
    return {
      fullName: t("common.unknown"),
      incidentStatus: t("common.unknown"),
    };
  }

  const { fullName } = getPatientName({ incidentWorkItem });

  const incidentStatus = getIncidentStatus(incidentWorkItem, t);

  return { fullName, incidentStatus };
};

export function FormProgressInner({
  user,
  incidentWorkItem,
  refetchWorkItem,
  onCancelTransfer,
  onEndIncident,
}: {
  user: ExtendedVisionUser | undefined;
  incidentWorkItem: IncidentWorkItemWithContext | undefined;
  refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
  onCancelTransfer?: FormProgressProps["onCancelTransfer"];
  onEndIncident?: FormProgressProps["onEndIncident"];
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notifications = useToastNotifications();

  const isOwner =
    incidentWorkItem !== undefined &&
    isCurrentOwner(incidentWorkItem, user?.id);

  const wasTransferred =
    incidentWorkItem !== undefined &&
    wasTransferredAway(incidentWorkItem, user?.id);

  const newOwnerName =
    incidentWorkItem === undefined
      ? undefined
      : getCurrentOwner(incidentWorkItem)?.name;

  // What an officer is told when the incident has moved on to someone else,
  // naming the new owner where they are known. Resolved to a string rather
  // than left as a call, so it compares by value.
  const transferAcceptedMessage =
    newOwnerName === undefined
      ? t("incidentWorkItem.transferAcceptedUnknownOfficer")
      : t("incidentWorkItem.transferAccepted", { officerName: newOwnerName });

  useEffect(() => {
    if (!incidentWorkItem || !user || isOwner) return;

    // An officer who no longer owns an incident keeps a read of it, and the
    // PDF is that read. Where there is no PDF to be had they keep nothing to
    // look at, so they belong somewhere they can act.
    if (hasTriageForm({ incidentWorkItem })) {
      navigate(
        createRoute.incidentPdfPage(incidentWorkItem.incidentWorkItem.id),
        { replace: true },
      );
      return;
    }

    // Leaving is right for any non-owner, but only an officer a transfer
    // moved off the incident can be told that is where it went.
    if (wasTransferred) {
      notifications.show(transferAcceptedMessage, { severity: "info" });
    }
    navigate(createRoute.home(), { replace: true });
  }, [
    user,
    incidentWorkItem,
    isOwner,
    navigate,
    notifications,
    transferAcceptedMessage,
    wasTransferred,
  ]);

  const [policePresenceUpdateDueStatus, setPolicePresenceUpdateDueStatus] =
    useState<ReturnType<typeof isPolicePresenceUpdateDue> | null>(
      incidentWorkItem
        ? isPolicePresenceUpdateDue({
            incidentWorkItem,
            updateIntervalMinutes:
              user?.sessionOrganisationConfiguration
                ?.intervalBetweenUpdateIncidentAppPromptMinutes,
            asOf: dayjs(),
          })
        : null,
    );

  useEffect(() => {
    const timer = setInterval(
      () => {
        if (incidentWorkItem) {
          setPolicePresenceUpdateDueStatus(
            isPolicePresenceUpdateDue({
              incidentWorkItem,
              updateIntervalMinutes:
                user?.sessionOrganisationConfiguration
                  ?.intervalBetweenUpdateIncidentAppPromptMinutes,
              asOf: dayjs(),
            }),
          );
        }
      },
      1000 * 60, // Update once a minute
    );
    return () => {
      clearInterval(timer);
    };
  }, [
    incidentWorkItem,
    user?.sessionOrganisationConfiguration
      ?.intervalBetweenUpdateIncidentAppPromptMinutes,
  ]);

  if ([user, incidentWorkItem].some((x) => x === undefined)) {
    return <CircularProgress />;
  }

  const actions =
    incidentWorkItem && user
      ? calculateActions({ incidentWorkItem, user, asOfDateTime: dayjs() })
      : defaultActions;

  const { fullName, incidentStatus } = extractIncidentDetails(
    incidentWorkItem,
    t,
  );

  const currentWorkFlowVersion =
    incidentWorkItem?.incidentWorkItem.workflowVersion;

  const currentWorkItemForm = (incidentWorkItem?.forms ?? []).find(
    (form) =>
      form.incidentWorkItemId === incidentWorkItem?.incidentWorkItem.id &&
      form.formDefinitionVersion === currentWorkFlowVersion &&
      form.status === "active",
  );

  if (currentWorkItemForm === undefined) {
    return <ErrorComponent />;
  }

  const loadWorkFlow = workflows[currentWorkItemForm.formDefinitionVersion];

  if (loadWorkFlow === undefined) {
    return <ErrorComponent />;
  }

  const createIncidentWorkFlow =
    loadWorkFlow[IncidentFormTypeSchema.enum.createIncident].formDefinition;

  const triageWorkFlow =
    loadWorkFlow[IncidentFormTypeSchema.enum.triage].formDefinition;

  const triageSchema = triageWorkFlow.sections
    .find((section) => section.id === FormSectionSchema.enum.triageMain)
    ?.buildFormSpecification({ incidentWorkItem }).formSchema;

  const triageSection: TriageIncidentMinimumFields | undefined = triageSchema
    ? ok(
        incidentWorkItem?.forms
          .find((form) => form.formType === IncidentFormTypeSchema.enum.triage)
          ?.sections.find(
            (section) => section.id === FormSectionSchema.enum.triageMain,
          )?.data ?? undefined,
      )
        .andThen(mustBeDefined())
        .andThen(mustParseAny(triageSchema))
        .unwrapOr(undefined)
    : undefined;

  const whatWillYouDo = match(triageSection?.whatWillYouDo.value)
    .with("136", () => "136" as const)
    .with("mca", () => "mentalCapacityAct" as const)
    .with("voluntary", "other", undefined, () => undefined)
    .exhaustive();

  // Dynamically load the current workflow based on what flow we have chosen from the triage form
  const loadCurrentWorkFlow =
    whatWillYouDo !== undefined
      ? loadWorkFlow[whatWillYouDo].formDefinition
      : undefined;

  const isTriageSectionComplete = () => {
    const formSectionSchema = triageWorkFlow.sections
      .find((section) => section.id === FormSectionSchema.enum.triageMain)
      ?.buildFormSpecification({ incidentWorkItem }).formSchema;

    const formSectionData =
      incidentWorkItem?.forms
        .find((form) => form.formType === IncidentFormTypeSchema.enum.triage)
        ?.sections.find(
          (section) => section.id === FormSectionSchema.enum.triageMain,
        )?.data ?? undefined;

    if (formSectionSchema === undefined || formSectionData === undefined) {
      return completionStatus.notStarted;
    }

    return formSectionSchema.safeParse(formSectionData).success
      ? completionStatus.complete
      : completionStatus.partiallyComplete;
  };

  // Merge createIncidentWorkFlow and triageWorkFlow sections with a `+` between their names
  const mergedCreateAndTriageSections = (() => {
    const triageSections = triageWorkFlow.sections ?? [];
    const createSections = createIncidentWorkFlow.sections ?? [];
    const triageCompletionStatus = isTriageSectionComplete();

    // Combine sections by index, with a `+` for overlapping sections
    const combinedSections = triageSections.map((section, index) => {
      const createSection = createSections[index];
      // if theres a triage section and create section, combine their names using a translation key
      if (createSection && section) {
        return {
          name: "incidentFormSections.incidentAndTriage" as TranslationKey,
          completionStatus: triageCompletionStatus,
        } as const;
      }
      return {
        name: section.name,
        completionStatus: triageCompletionStatus,
      } as const;
    });

    return combinedSections;
  })();

  const is136FormSectionComplete = (sectionId: IncidentFormSections[136]) => {
    const formSectionSchema = loadCurrentWorkFlow?.sections
      .find((section) => section.id === sectionId)
      ?.buildFormSpecification({ incidentWorkItem }).formSchema;

    const formSectionData =
      incidentWorkItem?.forms
        .find(
          (form) => form.formType === IncidentFormTypeSchema.enum.section136,
        )
        ?.sections.find((section) => section.id === sectionId)?.data ??
      undefined;

    if (formSectionSchema === undefined || formSectionData === undefined) {
      return completionStatus.notStarted;
    }

    return formSectionSchema.safeParse(formSectionData).success
      ? completionStatus.complete
      : completionStatus.partiallyComplete;
  };

  const isMcaFormSectionComplete = (
    sectionId: IncidentFormSections["mentalCapacityAct"],
  ) => {
    const formSectionSchema = loadCurrentWorkFlow?.sections
      .find((section) => section.id === sectionId)
      ?.buildFormSpecification({ incidentWorkItem }).formSchema;

    const formSectionData =
      incidentWorkItem?.forms
        .find(
          (form) =>
            form.formType === IncidentFormTypeSchema.enum.mentalCapacityAct,
        )
        ?.sections.find((section) => section.id === sectionId)?.data ??
      undefined;

    if (formSectionSchema === undefined || formSectionData === undefined) {
      return completionStatus.notStarted;
    }

    return formSectionSchema.safeParse(formSectionData).success
      ? completionStatus.complete
      : completionStatus.partiallyComplete;
  };

  const get136FormSectionClickHandler = (
    sectionId: IncidentFormSections["136"],
  ) => {
    if (!incidentWorkItem) {
      return;
    }

    const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;

    const route = match(sectionId)
      .with(FormSectionSchema.enum.personDetails, () =>
        createRoute.incidentPersonDetailsPage(incidentWorkItemId),
      )
      .with(FormSectionSchema.enum.detentionDetails, () =>
        createRoute.incidentDetentionDetailsPage(incidentWorkItemId),
      )
      .with(FormSectionSchema.enum.informationForHealthcare, () =>
        createRoute.incidentInformationForHealthcarePage(incidentWorkItemId),
      )
      .exhaustive();

    navigate(route);
  };

  const getMcaFormSectionClickHandler = (
    sectionId: IncidentFormSections["mentalCapacityAct"],
  ) => {
    if (!incidentWorkItem) {
      return;
    }

    const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;

    const route = match(sectionId)
      .with(FormSectionSchema.enum.mcaPersonDetails, () =>
        createRoute.incidentMcaPersonDetailsPage(incidentWorkItemId),
      )
      .with(FormSectionSchema.enum.mcaIncidentDetails, () =>
        createRoute.incidentMcaIncidentDetailsPage(incidentWorkItemId),
      )
      .with(FormSectionSchema.enum.mcaCapacityAssessment, () =>
        createRoute.incidentMcaCapacityAssessmentPage(incidentWorkItemId),
      )
      .with(FormSectionSchema.enum.mcaAccompaniment, () =>
        createRoute.incidentMcaAccompanimentPage(incidentWorkItemId),
      )
      .exhaustive();

    navigate(route);
  };

  // Combine all workflows into steps
  const currentSteps = [
    ...mergedCreateAndTriageSections.map(
      (section) =>
        ({
          label: section.name,
          completionStatus: section.completionStatus,
          onClick: () => {
            navigate(
              incidentWorkItem
                ? createRoute.incidentTriagePage(
                    incidentWorkItem.incidentWorkItem.id,
                  )
                : createRoute.home(),
            );
          },
        }) satisfies StepData,
    ),
    ...(loadCurrentWorkFlow?.sections ?? [])
      .filter(
        // We don't want to show the MCA accompaniment section here as it's handled via the Place of Safety flow
        // TODO: It would be better to have a data driven mechanism than hardcoding this exclusion in the frontend
        (section) => section.id !== FormSectionSchema.enum.mcaAccompaniment,
      )
      .map(
        (section) =>
          ({
            label: section.name,
            isDisabled: !actions.formDetails.canAdd,
            completionStatus: match(whatWillYouDo)
              .with("136", () =>
                is136FormSectionComplete(
                  section.id as IncidentFormSections["136"],
                ),
              )
              .with("mentalCapacityAct", () =>
                isMcaFormSectionComplete(
                  section.id as IncidentFormSections["mentalCapacityAct"],
                ),
              )
              .with(undefined, () => completionStatus.notStarted)
              .exhaustive(),
            onClick: () => {
              match(whatWillYouDo)
                .with("136", () => {
                  get136FormSectionClickHandler(
                    section.id as IncidentFormSections["136"],
                  );
                })
                .with("mentalCapacityAct", () => {
                  getMcaFormSectionClickHandler(
                    section.id as IncidentFormSections["mentalCapacityAct"],
                  );
                })
                .with(undefined, () => {})
                .exhaustive();
            },
          }) satisfies StepData,
      ),
  ];

  // Calculate key details about the latest Place of Safety
  const latestPlaceOfSafety = getLatestPlaceOfSafety(incidentWorkItem);
  const latestPolicePresenceRequest = getLatestPolicePresence(incidentWorkItem);

  return (
    <Stack
      data-testid="form-progress"
      sx={{
        [theme.breakpoints.up("md")]: {
          padding: "1rem",
        },
        [theme.breakpoints.down("md")]: {
          padding: "0.5rem",
        },
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
      }}
    >
      <Paper
        sx={{
          padding: { xs: 0, sm: 3 },
          mx: "auto",
          boxShadow: { xs: 0, sm: 3 },
          maxWidth: { xs: "100%", sm: "80%" },
          width: "100%",
        }}
      >
        {/* Incident Banner */}
        <IncidentBanner
          name={fullName}
          incidentWorkItem={incidentWorkItem}
          incidentStatus={incidentStatus}
          isViewShareFormDisabled={!actions.share.canShareForm}
          isViewPdfBtnDisabled={!actions.pdf.canView}
        />
        {/* Form Progress */}
        <FormProgress
          incidentWorkItem={incidentWorkItem}
          incidentWorkItemId={incidentWorkItem?.incidentWorkItem.id}
          steps={currentSteps}
          latestPlaceOfSafetyName={latestPlaceOfSafety?.name}
          latestPolicePresenceName={
            latestPolicePresenceRequest?.hasRemainedAtPlaceOfSafety
          }
          latestPolicePresenceUpdatedAt={
            latestPolicePresenceRequest?.createdAt
              ? latestPolicePresenceRequest.createdAt.toDate()
              : undefined
          }
          latestPlaceOfSafetyFormId={latestPlaceOfSafety?.arrivalFormId}
          arrivalUrl={
            incidentWorkItem?.incidentWorkItem.id !== undefined &&
            latestPlaceOfSafety !== undefined &&
            actions.arrival.canMarkArrival
              ? createRoute.incidentRecordPlaceOfSafetyArrivalPage(
                  incidentWorkItem.incidentWorkItem.id,
                  latestPlaceOfSafety.arrivalFormId,
                )
              : undefined
          }
          actions={actions}
          refetchWorkItem={refetchWorkItem}
          policePresenceUpdateDueStatus={policePresenceUpdateDueStatus}
          onCancelTransfer={onCancelTransfer}
          onEndIncident={onEndIncident}
        />
      </Paper>
    </Stack>
  );
}

export function FormProgressWrapper() {
  const { user, loading: isUserLoading, error: userError } = useUser();
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    {
      data: incidentWorkItem,
      loading: incidentWorkItemLoading,
      error: incidentWorkItemError,
    },
    refetchWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  if (isUserLoading || incidentWorkItemLoading) {
    return <CircularProgress />;
  }

  if (userError || incidentWorkItemError) {
    return <ErrorComponent />;
  }

  const render = ({
    user,
    incidentWorkItem,
    refetchWorkItem,
  }: {
    user: ExtendedVisionUser | undefined;
    incidentWorkItem: IncidentWorkItemWithContext | undefined;
    refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
  }) => {
    return (
      <FormProgressInner
        user={user}
        incidentWorkItem={incidentWorkItem}
        refetchWorkItem={refetchWorkItem}
      />
    );
  };
  return render({ user, incidentWorkItem, refetchWorkItem });
}

export function FormProgress({
  incidentWorkItem,
  incidentWorkItemId,
  steps,
  latestPlaceOfSafetyName,
  latestPlaceOfSafetyFormId,
  arrivalUrl,
  latestPolicePresenceName,
  latestPolicePresenceUpdatedAt,
  actions,
  refetchWorkItem,
  policePresenceUpdateDueStatus,
  onCancelTransfer,
  onEndIncident,
}: FormProgressProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [showTransferModal, setShowTransferModal] = useState(false);
  const notifications = useToastNotifications();
  const { t } = useTranslation();
  const workflowContext = getWorkflowTranslationContext({ incidentWorkItem });
  const tOptions = { context: workflowContext };

  const [isReviewBeforeEndingModalOpen, setIsReviewBeforeEndingModalOpen] =
    useState(false);

  const [showUnknownStatusDialog, setShowUnknownStatusDialog] = useState(false);

  const endingDestination = endIncidentDestination(actions.endIncident);
  const offersFinishForm = match(endingDestination)
    .with("finishForm", () => true)
    .with("preEndIncident", "preTriageDialog", "cannotEnd", () => false)
    .exhaustive();
  const startEnding = () => {
    match(endingDestination)
      .with("finishForm", "preEndIncident", () => {
        setIsReviewBeforeEndingModalOpen(true);
      })
      .with("preTriageDialog", () => {
        setShowUnknownStatusDialog(true);
      })
      .with("cannotEnd", () => {})
      .exhaustive();
  };

  return (
    <>
      {/* Form Progress Steps */}
      <Box sx={{ px: { xs: 1, sm: 2 } }}>
        <FormProgressSteps steps={steps} />
      </Box>
      {/* Place of Safety Section */}
      <Box
        sx={{
          mt: 2,
          width: "100%",
          px: { xs: 0, sm: 5 },
        }}
      >
        <Grid
          container
          data-testid="active-place-of-safety-section"
          sx={{
            backgroundColor: "common.background",
            width: "100%",
            p: 1.5,
            borderRadius: 2,
          }}
        >
          <Grid size={12}>
            <Box
              data-testid="active-place-of-safety-content"
              sx={{
                display: "grid",
                width: "100%",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                alignItems: { xs: "start", sm: "baseline" },
                columnGap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  minWidth: 0,
                  flexWrap: "wrap",
                }}
              >
                <TypographyI18N
                  sx={{
                    fontSize: { xs: "0.8125rem", sm: "1rem" },
                    fontWeight: 500,
                  }}
                  color="text.secondary"
                  translationKey={tk(
                    "formProgressPage.activePlaceOfSafety.title",
                  )}
                  translationOptions={tOptions}
                />
                <Typography
                  sx={{
                    marginLeft: "0.25rem",
                    fontWeight: "bold",
                    fontSize: { xs: "0.8125rem", sm: "1rem" },
                  }}
                >
                  {latestPlaceOfSafetyName ??
                    t(tk("formProgressPage.activePlaceOfSafety.notKnown"))}
                </Typography>
              </Box>
              <TypographyI18N
                sx={{
                  cursor: actions.placeOfSafety.canAdd
                    ? "pointer"
                    : "not-allowed",
                  textDecoration: "underline",
                  fontWeight: 500,
                  fontSize: "0.8125rem",
                  color: actions.placeOfSafety.canAdd
                    ? theme.palette.primary.main
                    : theme.palette.text.disabled,
                  display: "flex",
                  justifyContent: "flex-end",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
                translationKey={
                  latestPlaceOfSafetyFormId === undefined
                    ? tk("formProgressPage.add")
                    : tk("formProgressPage.update")
                }
                {...(!actions.placeOfSafety.canAdd
                  ? {}
                  : {
                      onClick: () => {
                        if (incidentWorkItemId !== undefined) {
                          if (
                            incidentWorkItem !== undefined &&
                            triageSelectionIsMca({
                              incidentWorkItem,
                            })
                          ) {
                            navigate(
                              createRoute.incidentMcaAccompanimentPage(
                                incidentWorkItemId,
                              ),
                            );
                            return;
                          }

                          if (latestPlaceOfSafetyFormId !== undefined) {
                            navigate(
                              createRoute.incidentReviewPlacesOfSafetyPage(
                                incidentWorkItemId,
                              ),
                            );
                          } else {
                            navigate(
                              createRoute.incidentRecordPlaceOfSafetyPage(
                                incidentWorkItemId,
                              ),
                            );
                          }
                        }
                      },
                    })}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      {/* Arrival and Handover Buttons Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 3,
          mb: { xs: 25, md: 0 },
        }}
      >
        <Grid
          container
          sx={{
            maxWidth: { xs: "100%" },
            mx: { xs: 0, sm: 5 },
          }}
        >
          <Grid
            container
            sx={{
              justifyContent: "space-around",
            }}
          >
            {/* Arrival Section */}
            <Grid size={5}>
              <Stack direction="column" spacing={1}>
                <TypographyI18N
                  sx={{
                    mb: 1,
                    fontSize: { xs: "0.8125", sm: "1rem" },
                    fontWeight: 600,
                  }}
                  translationKey={tk("formProgressPage.arrival")}
                />
                <TypographyI18N
                  variant="caption"
                  translationKey={tk("formProgressPage.confirmArrival")}
                  translationOptions={tOptions}
                />
                <Button
                  variant="contained"
                  data-testid="arrival-button"
                  disabled={arrivalUrl === undefined}
                  sx={{
                    mt: 2,
                    maxWidth: 300,
                    width: "100%",
                    paddingY: 1.5,
                    paddingX: 3,
                    minHeight: 48,
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                  onClick={() => {
                    if (arrivalUrl !== undefined) {
                      navigate(arrivalUrl);
                    }
                  }}
                >
                  <TypographyI18N
                    sx={{ fontWeight: 700, fontSize: "0.8125rem" }}
                    translationKey={tk("formProgressPage.arrival")}
                  />
                </Button>

                {/* Conditional message and icon when arrival form has been completed or not */}
                {actions.arrival.hasArrivedAtLatestPlaceOfSafety ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    <FormSectionCompleteIcon sx={{ color: "success.main" }} />
                    <TypographyI18N
                      variant="caption"
                      sx={{ fontWeight: 600, color: "text.primary" }}
                      translationKey={tk("formProgressPage.arrivalConfirmed")}
                    />
                  </Stack>
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    <TypographyI18N
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        fontStyle: "italic",
                        color: "text.disabled",
                      }}
                      translationKey={tk(
                        "formProgressPage.arrivalNotConfirmed",
                      )}
                      translationOptions={tOptions}
                    />
                  </Stack>
                )}
              </Stack>
            </Grid>

            <Grid sx={{ display: "flex", alignItems: "center" }}>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  borderColor: "divider",
                }}
              />
            </Grid>

            {/* Handover Section */}
            <Grid size={5}>
              <Stack direction="column" spacing={1}>
                <TypographyI18N
                  translationKey={tk("formProgressPage.shareWithPOS")}
                  translationOptions={tOptions}
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.8125", sm: "1rem" },
                    fontWeight: 600,
                    gap: 0.5,
                  }}
                />
                <TypographyI18N
                  variant="caption"
                  translationKey={tk("formProgressPage.confirmHandover")}
                  translationOptions={tOptions}
                />
                <Button
                  variant="contained"
                  data-testid="handover-button"
                  disabled={!actions.share.canShareForm}
                  sx={{
                    mt: 2,
                    maxWidth: 300,
                    width: "100%",
                    paddingY: 1.5,
                    paddingX: 3,
                    minHeight: 48,
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                  endIcon={
                    <img
                      src={HospitalIcon}
                      alt="share"
                      style={{
                        verticalAlign: "middle",
                        width: "1.25rem",
                        height: "1.25rem",
                        filter: "invert(100%)",
                      }}
                    />
                  }
                  onClick={() => {
                    if (incidentWorkItemId !== undefined) {
                      navigate(
                        createRoute.incidentSharePage(incidentWorkItemId),
                      );
                    }
                  }}
                >
                  <TypographyI18N
                    sx={{ fontWeight: 700, fontSize: "0.8125rem" }}
                    translationKey={tk("formProgressPage.share")}
                  />
                </Button>
                {/* Conditional message and icon when form has been shared or not */}
                {actions.share.hasSharedForm && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    <FormSectionCompleteIcon sx={{ color: "success.main" }} />
                    <TypographyI18N
                      variant="caption"
                      sx={{ fontWeight: 600, color: "text.primary" }}
                      translationKey={tk("formProgressPage.formShared")}
                    />
                  </Stack>
                )}
                {!actions.share.canShareForm && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    <TypographyI18N
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        fontStyle: "italic",
                        color: "text.disabled",
                      }}
                      translationKey={tk(
                        "formProgressPage.completeAllSections",
                      )}
                    />
                  </Stack>
                )}
              </Stack>
            </Grid>
            {actions.policePresence.canEnterOfficerRequestInformation && (
              <Stack
                sx={{
                  gap: "0.5rem",
                  display: "flex",
                  flexGrow: 1,
                  alignItems: "center",
                  marginTop: "1rem",
                }}
              >
                <TypographyI18N
                  sx={{
                    fontSize: { xs: "0.8125rem", sm: "1rem" },
                    fontWeight: 600,
                    gap: 0.5,
                    textTransform: "uppercase",
                  }}
                  translationKey={tk("formProgressPage.stillAtPlaceOfSafety")}
                  translationOptions={tOptions}
                />
                <Button
                  variant="contained"
                  data-testid="officer-request-police-presence-button"
                  disabled={
                    !actions.policePresence.canEnterOfficerRequestInformation
                  }
                  color={"success"}
                  sx={{
                    width: "100%",
                    paddingY: 1.5,
                    paddingX: 3,
                    minHeight: 48,
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                  onClick={() => {
                    if (incidentWorkItemId !== undefined) {
                      navigate(
                        createRoute.incidentPolicePresenceLogPage(
                          incidentWorkItemId,
                        ),
                      );
                    }
                  }}
                >
                  <TypographyI18N
                    sx={{ fontWeight: 700, fontSize: "0.8125rem" }}
                    translationKey={tk("formProgressPage.update")}
                  />
                </Button>
                {policePresenceUpdateDueStatus !== null &&
                  policePresenceUpdateDueStatus.status !== "not-applicable" && (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "center",
                      }}
                    >
                      {match(policePresenceUpdateDueStatus.status)
                        .with("not-updated", () => (
                          <WarningAmberIcon sx={{ color: "warning.main" }} />
                        ))
                        .with("not-due", () => (
                          <FormSectionCompleteIcon
                            sx={{ color: "success.main" }}
                          />
                        ))
                        .with("due", () => (
                          <AccessTimeIcon sx={{ color: "error.main" }} />
                        ))
                        .exhaustive()}
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        {policePresenceUpdateDueStatus.description}
                      </Typography>
                    </Stack>
                  )}
              </Stack>
            )}
          </Grid>
        </Grid>
      </Box>
      {/* End and Transfer Incident Buttons */}
      <Box
        sx={{
          position: {
            xs: "fixed", // Fixed at bottom on mobile
            sm: "sticky", // Sticky at bottom on tablet and up
          },
          bottom: 0,
          left: 0,
          width: {
            xs: "100%", // Full width on mobile
            sm: "auto", // Let it size normally on larger screens
          },
          zIndex: 1000,
          backgroundColor: "common.background",
          borderRadius: {
            xs: 0,
            sm: "9px",
          },
          borderTop: {
            xs: "1px solid #ddd", // Divider line on mobile
            sm: "none",
          },
        }}
      >
        <Grid
          container
          sx={{
            mt: { sm: 0, md: 3 },
            p: 2,
            flexDirection: "row",
            gap: { xs: 2, sm: 0 },
            justifyContent: "space-between",
            alignItems: "stretch",
          }}
        >
          <Grid
            size={{ xs: 12, sm: 5 }}
            sx={{
              display: "flex",
              justifyContent: { xs: "stretch", sm: "flex-start" },
            }}
          >
            {offersFinishForm ? (
              <Button
                variant="outlined"
                data-testid="finish-form-button"
                sx={{
                  width: "100%",
                  height: "100%",
                  fontSize: "0.75rem",
                  backgroundColor: "white",
                  color: theme.palette.common.teal,
                  borderColor: theme.palette.common.teal,
                  "&:hover": {
                    borderColor: theme.palette.common.deepTeal,
                  },
                }}
                disabled={!actions.endIncident.canEnd}
                onClick={startEnding}
              >
                <TypographyI18N
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  translationKey={tk("finishForm.finishFormTitle")}
                />
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  data-testid="end-incident-button"
                  sx={{
                    width: "100%",
                    height: "100%",
                    fontSize: "0.75rem",
                    backgroundColor: "white",
                  }}
                  disabled={!actions.endIncident.canEnd}
                  onClick={startEnding}
                >
                  <TypographyI18N
                    variant="caption"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                    translationKey={tk("formProgressPage.endForm")}
                  />
                </Button>
              </>
            )}
          </Grid>

          <Grid
            size={{ xs: 12, sm: 5 }}
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            {/* Transfer Button */}
            {actions.transfers.canRevoke ? (
              <SubmissionButton
                variant="outlined"
                color="primary"
                data-testid="cancel-transfer-button"
                sx={{
                  width: "100%",
                  height: "100%",
                  fontSize: "0.75rem",
                  backgroundColor: "white",
                }}
                onClick={async () => {
                  if (incidentWorkItemId) {
                    try {
                      const revokeTransfer =
                        onCancelTransfer ??
                        ((id: IncidentWorkItemId) =>
                          api.incidentWorkItem.revokeTransfer({
                            incidentWorkItemId: id,
                          }));
                      const res = await revokeTransfer(incidentWorkItemId);
                      if (res.status === 204 || res.status === 200) {
                        notifications.show(
                          t("incidentWorkItem.notificationCancelledShow"),
                          { severity: "success" },
                        );
                        await refetchWorkItem();
                      } else {
                        notifications.show(
                          t("incidentWorkItem.notificationCancelledError"),
                          { severity: "error" },
                        );
                        // A refusal means this page is acting on an
                        // incident that has moved on without it. Re-reading is
                        // the only thing that brings it back into step.
                        await refetchWorkItem();
                      }
                    } catch (_error) {
                      notifications.show(
                        t("incidentWorkItem.notificationCancelledError"),
                        { severity: "error" },
                      );
                    }
                  } else {
                    notifications.show(
                      t("incidentWorkItem.notificationCancelledError"),
                      { severity: "error" },
                    );
                  }
                }}
              >
                <TypographyI18N
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  translationKey={tk("formProgressPage.cancelTransfer")}
                />
              </SubmissionButton>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                data-testid="transfer-button"
                sx={{
                  width: "100%",
                  height: "100%",
                  fontSize: "0.75rem",
                  backgroundColor: "white",
                  color: "common.btpBlue",
                  borderColor: "common.btpBlue",
                }}
                disabled={!actions.transfers.canInitiate}
                onClick={() => {
                  setShowTransferModal(true);
                }}
                endIcon={
                  <img
                    src={PoliceIcon}
                    alt="share"
                    style={{
                      verticalAlign: "middle",
                      width: "1.25rem",
                      height: "1.25rem",
                    }}
                  />
                }
              >
                <TypographyI18N
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  translationKey={tk("formProgressPage.transfer")}
                />
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
      <InitiateTransferModal
        open={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
        }}
        incidentWorkItemId={incidentWorkItemId}
        searchUsers={createUserSearch()}
        onConfirm={async (email: string | undefined) => {
          if (incidentWorkItemId === undefined || email === undefined) return;

          const createTransfer = {
            receivingOfficerEmail: email,
            incidentWorkItemId,
          };

          await api.incidentWorkItem
            .createTransfer(createTransfer)
            .then((res) => {
              if (res.status === 204) {
                notifications.show(
                  t("transfer.initiateTransfer.confirmToast", { email }),
                  {
                    severity: "success",
                  },
                );
                setShowTransferModal(false);
                navigate(createRoute.home());
              } else {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                notifications.show(res.data.reason, {
                  severity: "error",
                });
              }
            });
        }}
      />
      <PopupDialog
        open={showUnknownStatusDialog}
        onClose={() => {
          setShowUnknownStatusDialog(false);
        }}
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "flex-end", padding: 0 }}
        >
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => {
              setShowUnknownStatusDialog(false);
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <FormWrapper>
          <Header
            title={buildTranslationInput(tk("endIncidentPreTriage.endForm"))}
            page={1}
            totalPages={1}
          />
          <Box sx={{ p: 2, my: 2 }}>
            <Banner
              body={tk("endIncidentPreTriage.warningBanner")}
              bannerType={"error"}
            />
          </Box>
          <Footer
            onCancel={{
              handler: () => {
                setShowUnknownStatusDialog(false);
                if (incidentWorkItemId !== undefined) {
                  navigate(createRoute.incidentTriagePage(incidentWorkItemId));
                }
              },
            }}
            onNext={{
              handler: async () => {
                if (!incidentWorkItemId) {
                  notifications.show(
                    t("endIncidentPreTriage.noIncidentIdError"),
                    {
                      severity: "error",
                    },
                  );
                  return;
                }
                const endIncident =
                  onEndIncident ??
                  ((id: IncidentWorkItemId) =>
                    api.incidentWorkItem.endIncident(id));
                await endIncident(incidentWorkItemId)
                  .then((res) => {
                    if (res.status === 200 || res.status === 204) {
                      notifications.show(
                        t("endIncidentPreTriage.confirmToast"),
                        { severity: "success" },
                      );
                      setShowUnknownStatusDialog(false);
                      navigate(createRoute.home());
                    } else {
                      notifications.show(
                        res.data?.reason ??
                          t("endIncidentPreTriage.errorToast"),
                        { severity: "error" },
                      );
                      void refetchWorkItem();
                    }
                  })
                  .catch(() => {
                    notifications.show(t("endIncidentPreTriage.errorToast"), {
                      severity: "error",
                    });
                  });
              },
              label: tk("endIncidentPreTriage.endForm"),
            }}
          />
        </FormWrapper>
      </PopupDialog>
      {isReviewBeforeEndingModalOpen && (
        <ReviewBeforeEndingModal
          open={isReviewBeforeEndingModalOpen}
          navigate={navigate}
          onClose={() => {
            setIsReviewBeforeEndingModalOpen(false);
          }}
          canEnterOfficerRequestInformation={
            actions.policePresence.canEnterOfficerRequestInformation
          }
          latestPlaceOfSafetyName={latestPlaceOfSafetyName}
          latestPolicePresenceName={latestPolicePresenceName}
          latestPolicePresenceUpdatedAt={latestPolicePresenceUpdatedAt}
          incidentWorkItem={incidentWorkItem}
          onContinue={() => {
            setIsReviewBeforeEndingModalOpen(false);
            if (incidentWorkItemId !== undefined) {
              match(endingDestination)
                .with("finishForm", "preEndIncident", (destination) => {
                  navigate(endIncidentRoute(incidentWorkItemId, destination));
                })
                .with("preTriageDialog", () => {
                  setShowUnknownStatusDialog(true);
                })
                .with("cannotEnd", () => {})
                .exhaustive();
            }
          }}
        />
      )}
    </>
  );
}
